import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Konva from 'konva';
import './CoverDesigner.css';
import { useCanvasState } from './hooks/useCanvasState';
import { useExportAndSave, CoverPageDimensions } from './hooks/useExportAndSave';
import { CanvasState, computeCoverDimensions, BLEED_MARGIN } from './types';
import { baseUrl } from '../../api/api';
import DesignerToolbar from './components/DesignerToolbar';
import DesignerCanvas from './components/DesignerCanvas';
import LayersPanel from './components/LayersPanel';
import PropertiesPanel from './components/PropertiesPanel';
import TemplatesPicker from './components/TemplatesPicker';
import AIGeneratePanel, { CoverSide } from './components/AIGeneratePanel';
import CoverPreview from './components/CoverPreview';
import ISBNModal from './components/ISBNModal';

const CoverDesigner: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookId      = searchParams.get('bookId')      || '';
  const bookSize    = searchParams.get('bookSize')    || '6x9';
  const bookTitle   = searchParams.get('title')       || '';
  const bookAuthor  = searchParams.get('author')      || '';
  const paperType   = searchParams.get('paperType')   || 'Cream Paper';
  const bindingType = searchParams.get('bindingType') || 'Paperback';
  const pageCount   = parseInt(searchParams.get('pageCount') || '100', 10);

  const stageRef = useRef<Konva.Stage | null>(null);
  const [showTemplates,      setShowTemplates]      = useState(false);
  const [showAIPanel,        setShowAIPanel]        = useState(false);
  const [showPreview,        setShowPreview]        = useState(false);
  const [showISBNModal,      setShowISBNModal]      = useState(false);
  const [aiGenerationsUsed,  setAiGenerationsUsed]  = useState(0);
  const [aiGenerationsLimit, setAiGenerationsLimit] = useState(2);

  const LS_KEY = `coverDesigner_${bookId}`;

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, []);

  const {
    state,
    addElement,
    updateElement,
    updateElementLive,
    deleteElement,
    reorderElements,
    setSelectedId,
    setBackground,
    setBackgroundLive,
    loadState,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useCanvasState(bookSize, paperType, bindingType, pageCount);

  const pageDims: CoverPageDimensions = useMemo(() => {
    const dim = computeCoverDimensions(bookSize, paperType, bindingType, pageCount);
    return { pageW: dim.pageW, pageH: dim.pageH, bleed: BLEED_MARGIN };
  }, [bookSize, paperType, bindingType, pageCount]);

  const { exportAndSave, downloadPNG, downloadPDF, isSaving, saveError, saveSuccess } = useExportAndSave(stageRef, pageDims);

  const selectedElement = useMemo(
    () => state.elements.find(el => el.id === state.selectedId) ?? null,
    [state.elements, state.selectedId],
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (
        ((e.ctrlKey || e.metaKey) && e.key === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z')
      ) {
        e.preventDefault();
        redo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        if (state.selectedId) {
          const original = state.elements.find(el => el.id === state.selectedId);
          if (original) {
            const clone = { ...original, id: `el_${Date.now()}`, x: original.x + 20, y: original.y + 20, name: `${original.name} copy` };
            addElement(clone);
            setSelectedId(clone.id);
          }
        }
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && state.selectedId) {
        const target = state.elements.find(el => el.id === state.selectedId);
        if (target?.locked) return;
        e.preventDefault();
        deleteElement(state.selectedId);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo, deleteElement, addElement, setSelectedId, state.selectedId, state.elements]);

  // Load persisted state on mount — server is authoritative, localStorage is fast fallback
  useEffect(() => {
    if (!bookId) return;

    const restore = (saved: { elements: any[]; background: string }) => {
      loadState({
        elements: saved.elements ?? [],
        selectedId: null,
        background: saved.background ?? '#ffffff',
        stageWidth: state.stageWidth,
        stageHeight: state.stageHeight,
        guides: state.guides,
      });
    };

    fetch(`${baseUrl}/api/covers/designer-state/${bookId}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.state?.elements?.length) {
          restore(data.state);
          localStorage.setItem(LS_KEY, JSON.stringify(data.state));
        } else {
          const cached = localStorage.getItem(LS_KEY);
          if (cached) restore(JSON.parse(cached));
        }
        if (typeof data.generationsUsed === 'number') {
          setAiGenerationsUsed(data.generationsUsed);
        }
        if (typeof data.generationsLimit === 'number') {
          setAiGenerationsLimit(data.generationsLimit);
        }
      })
      .catch(() => {
        const cached = localStorage.getItem(LS_KEY);
        if (cached) { try { restore(JSON.parse(cached)); } catch {} }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  // Debounced save to localStorage on every canvas change
  useEffect(() => {
    if (!bookId) return;
    const t = setTimeout(() => {
      localStorage.setItem(LS_KEY, JSON.stringify({
        elements: state.elements,
        background: state.background,
      }));
    }, 800);
    return () => clearTimeout(t);
  }, [state.elements, state.background, bookId, LS_KEY]);

  const persistState = useCallback(() => {
    if (!bookId) return;
    fetch(`${baseUrl}/api/covers/designer-state`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        bookId,
        state: { elements: state.elements, background: state.background },
      }),
    }).catch(console.error);
  }, [bookId, state.elements, state.background, authHeaders]);

  // Save only — stays in designer, shows ✓ banner briefly
  const handleSave = useCallback(() => {
    persistState();
    exportAndSave(bookId);
  }, [bookId, persistState, exportAndSave]);

  const goToBookCover = useCallback(() => {
    navigate(`/books?id=${bookId}&step=book-cover`);
  }, [navigate, bookId]);

  // Save & exit — saves then lands on Book Cover tab
  const handleSaveAndExit = useCallback(() => {
    persistState();
    exportAndSave(bookId, goToBookCover);
  }, [bookId, persistState, exportAndSave, goToBookCover]);

  const handleApplyTemplate = (partial: Partial<CanvasState>) => {
    const next: CanvasState = {
      elements: partial.elements ?? [],
      selectedId: null,
      background: partial.background ?? '#ffffff',
      stageWidth: state.stageWidth,
      stageHeight: state.stageHeight,
      guides: state.guides,
    };
    loadState(next);
  };

  const handleAddISBN = useCallback(() => setShowISBNModal(true), []);

  const placeISBNElement = useCallback((src: string) => {
    const { guides, stageHeight: sH } = state;
    const { bleedPx, backWidthPx } = guides;
    const bW = Math.round(backWidthPx * 0.24);
    const bH = Math.round(bW / 1.42);
    const margin = Math.round(backWidthPx * 0.04);
    addElement({
      id: `el_${Date.now()}`,
      type: 'image',
      name: 'ISBN Barcode',
      x: bleedPx + backWidthPx - bW - margin,
      y: sH - bleedPx - bH - margin,
      width: bW,
      height: bH,
      rotation: 0,
      opacity: 1,
      visible: true,
      src,
      flipX: false,
      flipY: false,
    });
    setShowISBNModal(false);
  }, [state, addElement]);

  const handleISBNGenerate = useCallback((isbn: string) => {
    import('jsbarcode').then(({ default: JsBarcode }) => {
      const { guides } = state;
      const { backWidthPx } = guides;
      const bW = Math.round(backWidthPx * 0.24);
      const bH = Math.round(bW / 1.42);

      // Generate at 10× display size for print-quality sharpness.
      // The element displays at bW×bH but the image source has 10× the pixels,
      // so it stays crisp even at high DPI PDF export.
      const Q = 10;
      const margin = Math.round(bW * 0.05) * Q;
      const moduleW = Math.max(1, Math.floor((bW * Q - margin * 2) / 95));

      const canvas = document.createElement('canvas');
      JsBarcode(canvas, isbn, {
        format: 'EAN13',
        width: moduleW,
        height: Math.round(bH * 0.72 * Q),
        displayValue: true,
        fontSize: Math.max(8, Math.round(bH * 0.11 * Q)),
        textMargin: 3 * Q,
        margin,
        background: '#ffffff',
        lineColor: '#111111',
      });
      placeISBNElement(canvas.toDataURL('image/png'));
    });
  }, [state, placeISBNElement]);

  const handleISBNPlaceholder = useCallback(() => {
    const { guides } = state;
    const { backWidthPx } = guides;
    const bW = Math.round(backWidthPx * 0.24);
    const bH = Math.round(bW / 1.42);

    // Same 10× oversampling so the placeholder is equally sharp
    const Q  = 10;
    const cW = bW * Q;
    const cH = bH * Q;
    const c  = document.createElement('canvas');
    c.width  = cW;
    c.height = cH;
    const ctx = c.getContext('2d')!;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cW, cH);

    const barH    = Math.round(cH * 0.66);
    const marginX = Math.round(cW * 0.05);
    const barAreaW = cW - marginX * 2;
    const modules = [
      1,1,1,
      3,2,1,1,2,1,3,1,1,2,1,2,1,2,3,1,1,1,2,1,1,2,1,
      1,1,1,1,1,
      2,1,1,2,1,1,3,1,2,1,1,2,1,3,1,1,1,2,1,1,2,1,1,
      3,1,1,
    ];
    const total = modules.reduce((a, b) => a + b, 0);
    const unitW = barAreaW / total;
    ctx.fillStyle = '#111111';
    let cx = marginX;
    modules.forEach((w, i) => {
      if (i % 2 === 0) ctx.fillRect(cx, 0, Math.max(1, Math.round(unitW * w)), barH);
      cx += unitW * w;
    });

    const labelSize = Math.max(7 * Q, Math.round(cH * 0.11));
    ctx.fillStyle = '#111111';
    ctx.font = `bold ${labelSize}px Arial, sans-serif`;
    ctx.textAlign = 'left';
    ctx.fillText('ISBN', marginX, barH + labelSize + 2 * Q);

    const numSize = Math.max(6 * Q, Math.round(cH * 0.10));
    ctx.font = `${numSize}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText('978-0-000000-00-0', cW / 2, barH + labelSize + numSize + 6 * Q);

    placeISBNElement(c.toDataURL('image/png'));
  }, [state, placeISBNElement]);

  const handleMoveUp = (index: number) => {
    if (index < state.elements.length - 1) reorderElements(index, index + 1);
  };

  const handleMoveDown = (index: number) => {
    if (index > 0) reorderElements(index, index - 1);
  };

  const handleAddAIImage = (src: string, coverSide: CoverSide) => {
    const { guides, stageWidth, stageHeight } = state;

    // Determine target zone on the canvas for each cover side
    const zones: Record<CoverSide, { x: number; y: number; w: number; h: number; label: string }> = {
      front: {
        x: guides.frontStartPx,
        y: 0,
        w: stageWidth - guides.frontStartPx,
        h: stageHeight,
        label: 'AI Front',
      },
      back: {
        x: 0,
        y: 0,
        w: guides.bleedPx + guides.backWidthPx,
        h: stageHeight,
        label: 'AI Back',
      },
      fullwrap: {
        x: 0,
        y: 0,
        w: stageWidth,
        h: stageHeight,
        label: 'AI Full Wrap',
      },
    };

    const zone = zones[coverSide];

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const imgAspect  = img.width / img.height;
      const zoneAspect = zone.w / zone.h;

      // Scale to FILL the zone (cover strategy — no white gaps, may slightly overflow)
      let finalW: number;
      let finalH: number;
      if (imgAspect > zoneAspect) {
        // Image wider than zone — fit by height, centre horizontally
        finalH = zone.h;
        finalW = zone.h * imgAspect;
      } else {
        // Image taller than zone — fit by width, centre vertically
        finalW = zone.w;
        finalH = zone.w / imgAspect;
      }

      addElement({
        id: `el_${Date.now()}`,
        type: 'image',
        name: zone.label,
        x: zone.x + (zone.w - finalW) / 2,
        y: zone.y + (zone.h - finalH) / 2,
        width: finalW,
        height: finalH,
        rotation: 0,
        opacity: 1,
        visible: true,
        src,
        flipX: false,
        flipY: false,
      });
    };
    img.src = src;
  };

  return (
    <div className="cover-designer-root">
      <DesignerToolbar
        background={state.background}
        stageWidth={state.stageWidth}
        stageHeight={state.stageHeight}
        isSaving={isSaving}
        canUndo={canUndo}
        canRedo={canRedo}
        showAIPanel={showAIPanel}
        onAddElement={addElement}
        onSetBackground={setBackground}
        onSetBackgroundLive={setBackgroundLive}
        onUndo={undo}
        onRedo={redo}
        onSave={handleSave}
        onSaveAndExit={handleSaveAndExit}
        onBack={goToBookCover}
        onShowTemplates={() => setShowTemplates(true)}
        onToggleAIPanel={() => setShowAIPanel(v => !v)}
        onShowPreview={() => setShowPreview(true)}
        onAddISBN={handleAddISBN}
      />

      <div className="cover-designer-body">
        <LayersPanel
          elements={state.elements}
          selectedId={state.selectedId}
          onSelectId={setSelectedId}
          onToggleVisible={id => {
            const el = state.elements.find(e => e.id === id);
            if (el) updateElement(id, { visible: !el.visible });
          }}
          onToggleLock={id => {
            const el = state.elements.find(e => e.id === id);
            if (el) updateElement(id, { locked: !el.locked });
          }}
          onDelete={deleteElement}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          onRename={(id, name) => updateElement(id, { name })}
        />

        <div className="cover-designer-canvas-area">
          <div>
            <DesignerCanvas
              elements={state.elements}
              selectedId={state.selectedId}
              background={state.background}
              stageWidth={state.stageWidth}
              stageHeight={state.stageHeight}
              guides={state.guides}
              stageRef={stageRef}
              onSelectId={setSelectedId}
              onUpdateElement={updateElement}
            />
            {saveError && (
              <p className="text-red-500 text-sm mt-3 text-center">{saveError}</p>
            )}
            {saveSuccess && (
              <div className="mt-3 flex items-center justify-center gap-3 px-4 py-2.5 bg-green-50 border border-green-200 rounded-lg text-sm">
                <span className="text-green-700 font-medium">Cover saved successfully</span>
                <a
                  href={`/books?id=${bookId}`}
                  className="text-orange-600 hover:text-orange-700 font-semibold underline underline-offset-2"
                >
                  Go to book →
                </a>
              </div>
            )}
          </div>
        </div>

        {showAIPanel ? (
          <AIGeneratePanel
            bookTitle={bookTitle}
            bookAuthor={bookAuthor}
            bookId={bookId}
            generationsUsed={aiGenerationsUsed}
            generationsLimit={aiGenerationsLimit}
            onAddImage={handleAddAIImage}
            onClose={() => setShowAIPanel(false)}
            onGenerationsUpdate={(used, limit) => {
              setAiGenerationsUsed(used);
              setAiGenerationsLimit(limit);
            }}
          />
        ) : (
          <div className="properties-panel">
            <PropertiesPanel
              selectedElement={selectedElement}
              background={state.background}
              onUpdate={updateElement}
              onUpdateLive={updateElementLive}
              onSetBackground={setBackground}
              onSetBackgroundLive={setBackgroundLive}
            />
          </div>
        )}
      </div>

      {showISBNModal && (
        <ISBNModal
          onGenerate={handleISBNGenerate}
          onPlaceholder={handleISBNPlaceholder}
          onClose={() => setShowISBNModal(false)}
        />
      )}

      {showPreview && (
        <CoverPreview
          stageRef={stageRef}
          guides={state.guides}
          stageWidth={state.stageWidth}
          stageHeight={state.stageHeight}
          onClose={() => setShowPreview(false)}
        />
      )}

      {showTemplates && (
        <TemplatesPicker
          stageWidth={state.stageWidth}
          stageHeight={state.stageHeight}
          guides={state.guides}
          bookTitle={bookTitle}
          bookAuthor={bookAuthor}
          onApply={handleApplyTemplate}
          onClose={() => setShowTemplates(false)}
        />
      )}
    </div>
  );
};

export default CoverDesigner;
